import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PublicLayout";
import { TextField, TextAreaField, SelectField } from "@/components/FormKit";
import { SuccessScreen } from "@/pages/BookRide";
import { COMPANY } from "@/lib/data";

const empty = {
  name: "", phone: "", email: "", pickup_address: "", destination: "",
  date: "", pickup_time: "", transportation_type: "", trip_type: "", notes: "",
};

export default function PrivatePay() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => set(k)(e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup_address || !form.destination || !form.date || !form.transportation_type || !form.trip_type) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Quote request submitted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (done) return <SuccessScreen title="Quote Request Received" message="Thanks! We'll review your request and follow up with transportation options and pricing." />;

  return (
    <>
      <PageHero eyebrow="Private Pay Transportation" title="Request a Transportation Quote"
        subtitle="Convenient transportation for individuals, families, and caregivers who need reliable rides outside of broker-arranged trips. Tell us about your trip and we'll follow up with options." />
      <section className="bg-brand-bg py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <form onSubmit={submit} data-testid="quote-form" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <TextField label="Name" required testid="quote-name" value={form.name} onChange={setE("name")} />
              <TextField label="Phone" required testid="quote-phone" type="tel" value={form.phone} onChange={setE("phone")} />
              <TextField label="Email" testid="quote-email" type="email" wrapClass="sm:col-span-2" value={form.email} onChange={setE("email")} />
              <TextField label="Pickup Address" required testid="quote-pickup" wrapClass="sm:col-span-2" value={form.pickup_address} onChange={setE("pickup_address")} />
              <TextField label="Destination" required testid="quote-destination" wrapClass="sm:col-span-2" value={form.destination} onChange={setE("destination")} />
              <TextField label="Date" required testid="quote-date" type="date" value={form.date} onChange={setE("date")} />
              <TextField label="Pickup Time" testid="quote-time" type="time" value={form.pickup_time} onChange={setE("pickup_time")} />
              <SelectField label="Transportation Type" required testid="quote-type" value={form.transportation_type}
                onValueChange={set("transportation_type")} placeholder="Select type" options={["Ambulatory", "Wheelchair", "Other"]} />
              <SelectField label="One-Way or Round Trip" required testid="quote-trip" value={form.trip_type}
                onValueChange={set("trip_type")} placeholder="Select trip" options={["One Way", "Round Trip"]} />
              <TextAreaField label="Special Transportation Notes" testid="quote-notes" wrapClass="sm:col-span-2"
                value={form.notes} onChange={setE("notes")} placeholder="Anything we should know to serve you best…" />
            </div>
            <Button type="submit" disabled={loading} data-testid="quote-submit"
              className="mt-6 h-14 w-full rounded-full bg-brand-purple text-base font-semibold text-white hover:bg-brand-purple2">
              {loading ? "Submitting…" : "Request Transportation Quote"}
            </Button>
            <p className="mt-4 text-center text-sm text-slate-500">
              Questions? Call <a href={COMPANY.phoneHref} className="font-semibold text-brand-blue">{COMPANY.phone}</a>
            </p>
          </form>
        </div>
      </section>
    </>
  );
}
